/**
 * card-gallery-ios-scroll-fix.js  (v2)
 * ------------------------------------------------------------------------
 * Хотфикс для Horizon: на iOS (WebKit) карусель фото внутри карточки товара
 * показывает не главный, а случайный кадр того же SKU.
 *
 * Корневая причина (v1 лечила не тот момент):
 * WebKit восстанавливает scrollLeft вложенных скроллеров при возврате
 * по истории/bfcache и при re-snap (scroll-snap-type: x mandatory) во время
 * лейаут-сдвигов. Ключевое: все slideshow стартуют с overflow: hidden
 * (см. slideshow-component:not([in-viewport]) в base.css), поэтому WebKit
 * ОТКЛАДЫВАЕТ восстановление позиции до момента, когда галерея войдёт во
 * вьюпорт и станет скроллируемой — т.е. ПОЗЖЕ любых фиксов на load/pageshow.
 * Штатный slideshow.js принимает такой «призрачный» скролл за пользовательский
 * (#handleScroll -> #sync) и никогда его не оспаривает.
 *
 * Стратегия v2: не «поправить один раз после загрузки», а непрерывно
 * различать легитимные скроллы и призрачные:
 *  - легитимный программный выбор слайда (свотчи вариантов, initial-slide,
 *    hover-превью) приходит событием 'slideshow:select' с trigger 'select' —
 *    запоминаем его слайд как ожидаемый;
 *  - реальный свайп пользователя ИМЕННО по этой галерее = touchstart внутри
 *    неё + горизонтальное движение (в v1 любой тап/вертикальный скролл
 *    страницы пальцем по фото навсегда исключал галерею из выравнивания —
 *    из-за этого фикс и «не работал»);
 *  - любой другой scroll на скроллере галереи — призрак WebKit: мгновенно
 *    возвращаем ожидаемый слайд.
 *
 * Не модифицирует slideshow.js, переживает обновления темы.
 * Ставится отдельным ассетом + одна строка подключения в theme.liquid.
 * ------------------------------------------------------------------------
 */
(() => {
  // Только WebKit на iOS/iPadOS. На Blink/Gecko бага нет — не вмешиваемся.
  const isWebKit = /AppleWebKit/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS|Edg/.test(navigator.userAgent);
  const isIOS =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS 13+ маскируется под Mac, отличаем по тач-поинтам
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isWebKit || !isIOS) return;

  const GALLERY_SCROLLER = '.card-gallery slideshow-slides';
  const DRAG_THRESHOLD = 8; // px горизонтального движения = осознанный свайп

  /** Ожидаемый слайд каждой галереи (задан программным select). @type {WeakMap<Element, Element>} */
  const expectedSlide = new WeakMap();
  /** Галереи, которые пользователь реально свайпал — их позицию не трогаем. */
  const userOwned = new WeakSet();

  /** @param {HTMLElement} scroller */
  const targetSlideOf = (scroller) => {
    const expected = expectedSlide.get(scroller);
    if (expected && expected.isConnected && !(expected.hidden && !expected.hasAttribute('reveal'))) {
      return expected;
    }
    // По умолчанию — первый видимый слайд (= featured media либо выбранный вариант).
    return scroller.querySelector('slideshow-slide:not([hidden]), slideshow-slide[reveal]');
  };

  /** @param {HTMLElement} scroller */
  const realign = (scroller) => {
    if (userOwned.has(scroller)) return;
    const target = targetSlideOf(scroller);
    if (!target) return;

    const desired = /** @type {HTMLElement} */ (target).offsetLeft;
    if (Math.abs(scroller.scrollLeft - desired) > 1) {
      // instant обязателен: на скроллере висит scroll-behavior: smooth.
      scroller.scrollTo({ left: desired, behavior: 'instant' });
    }
  };

  const realignAll = () => {
    for (const scroller of document.querySelectorAll(GALLERY_SCROLLER)) {
      realign(/** @type {HTMLElement} */ (scroller));
    }
  };

  // --- 1. Легитимные программные выборы слайда -----------------------------
  // select() темы шлёт всплывающее 'slideshow:select'; trigger 'select' =
  // программный (вариант/initial-slide/hover), 'drag' = осознанный drag.
  // trigger 'scroll' сюда не записываем: его порождают и призрачные скроллы.
  document.addEventListener('slideshow:select', (event) => {
    const component = /** @type {Element} */ (event.target);
    const scroller = component.querySelector?.('slideshow-slides');
    if (!scroller || !scroller.matches(GALLERY_SCROLLER)) return;

    const detail = /** @type {CustomEvent} */ (event).detail || /** @type {any} */ (event).detail;
    if (detail?.trigger === 'drag') {
      userOwned.add(scroller);
    } else if (detail?.trigger === 'select' && detail.slide) {
      expectedSlide.set(scroller, detail.slide);
    }
  });

  // --- 2. Реальные свайпы пользователя -------------------------------------
  // Помечаем галерею занятой пользователем только при горизонтальном движении
  // внутри неё. Тап и вертикальный скролл страницы пальцем по фото — не в счёт.
  /** @type {{scroller: HTMLElement, x: number, y: number} | null} */
  let touch = null;

  document.addEventListener(
    'touchstart',
    (event) => {
      const target = /** @type {Element} */ (event.target);
      const scroller = /** @type {HTMLElement | null} */ (target.closest?.(GALLERY_SCROLLER));
      touch = scroller ? { scroller, x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!touch) return;
      const dx = Math.abs(event.touches[0].clientX - touch.x);
      const dy = Math.abs(event.touches[0].clientY - touch.y);
      if (dx > DRAG_THRESHOLD && dx > dy) {
        userOwned.add(touch.scroller);
        touch = null;
      } else if (dy > DRAG_THRESHOLD && dy > dx) {
        touch = null; // вертикальный скролл страницы — не свайп галереи
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener('touchend', () => setTimeout(() => (touch = null), 100), {
    capture: true,
    passive: true,
  });

  // --- 3. Призрачные скроллы WebKit -----------------------------------------
  // scroll не всплывает, но виден capture-слушателю на document. Всё, что
  // скроллит галерею без активного касания и не совпадает с ожидаемым
  // слайдом, — откладённое восстановление/re-snap WebKit: выравниваем сразу.
  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = /** @type {HTMLElement} */ (event.target);
      if (!(scroller instanceof Element) || !scroller.matches?.(GALLERY_SCROLLER)) return;
      if (touch && touch.scroller === scroller) return; // касание в процессе
      realign(scroller);
    },
    { capture: true, passive: true }
  );

  // --- 4. Стартовые/страховочные проходы ------------------------------------
  // pageshow покрывает и первичную загрузку, и возврат из bfcache.
  window.addEventListener('pageshow', () => {
    realignAll();
    requestAnimationFrame(() => requestAnimationFrame(realignAll));
    setTimeout(realignAll, 300);
  });

  window.addEventListener('load', () => setTimeout(realignAll, 0));
})();
